import { httpClient, uploadFileWithTicket } from "@bw/shared/api";
import type { MediaUploadResponse, Schema } from "@bw/shared/types/generated";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";

type Photo = Schema["FacilityPhoto"];
type PhotoForm = Schema["FacilityPhotosReplace"];
type Props = { facilityId: string; canManage: boolean };
const photoKey = (id: string) => ["facility", id, "photos"];

export function FacilityPhotos(props: Props) {
  return <FacilityPhotosLoader key={props.facilityId} {...props} />;
}

function FacilityPhotosLoader({ facilityId, canManage }: Props) {
  const query = useQuery({
    queryKey: photoKey(facilityId),
    queryFn: () => httpClient.get<Photo[]>(`/facilities/${facilityId}/photos`),
    enabled: canManage,
  });
  if (!canManage) return null;
  if (query.isPending)
    return <Typography role="status">Loading photos…</Typography>;
  if (query.isError)
    return (
      <Alert
        severity="error"
        action={
          <Button size="small" onClick={() => void query.refetch()}>
            Retry
          </Button>
        }
      >
        Could not load facility photos.
      </Alert>
    );
  return <PhotoEditor facilityId={facilityId} initialPhotos={query.data} />;
}

function PhotoEditor({
  facilityId,
  initialPhotos,
}: {
  facilityId: string;
  initialPhotos: Photo[];
}) {
  const client = useQueryClient();
  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm<PhotoForm>({
    defaultValues: { photos: initialPhotos },
  });
  const { fields, append, move, remove } = useFieldArray({
    control,
    name: "photos",
  });
  const cover = watch("photos")?.[0];
  const [previewShape, setPreviewShape] = useState<"desktop" | "mobile">(
    "desktop",
  );
  const [busy, setBusy] = useState<"upload" | "save" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const locked = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  async function upload(file: File) {
    if (locked.current || fields.length >= 12) return;
    locked.current = true;
    setBusy("upload");
    setError(null);
    setSaved(false);
    try {
      const params = new URLSearchParams({
        content_type: file.type,
        content_length: String(file.size),
      });
      const ticket = await httpClient.post<MediaUploadResponse>(
        `/media/facilities/${facilityId}/photos?${params}`,
        {},
      );
      if (!mounted.current) return;
      const uploaded = await uploadFileWithTicket(file, ticket);
      if (mounted.current) append({ url: uploaded.publicUrl, alt_text: "" });
    } catch (cause) {
      if (mounted.current)
        setError(
          cause instanceof Error
            ? cause.message
            : "Could not upload photo. Choose the file again to retry.",
        );
    } finally {
      locked.current = false;
      if (mounted.current) setBusy(null);
    }
  }

  async function save(data: PhotoForm) {
    if (locked.current || !mounted.current) return;
    locked.current = true;
    setBusy("save");
    setError(null);
    setSaved(false);
    try {
      const result = await httpClient.put<Photo[]>(
        `/facilities/${facilityId}/photos`,
        {
          photos: data.photos.map((photo) => ({
            url: photo.url,
            alt_text: photo.alt_text.trim(),
          })),
        } satisfies PhotoForm,
      );
      client.setQueryData(photoKey(facilityId), result);
      if (mounted.current) {
        reset({ photos: result });
        setSaved(true);
      }
    } catch (cause) {
      if (mounted.current)
        setError(
          cause instanceof Error
            ? cause.message
            : "Could not save photos. Try saving again.",
        );
    } finally {
      locked.current = false;
      if (mounted.current) setBusy(null);
    }
  }

  return (
    <Stack
      component="section"
      aria-labelledby="facility-photos-title"
      spacing={2}
    >
      <Typography
        id="facility-photos-title"
        component="h2"
        variant="h6"
        sx={{ fontWeight: 800 }}
      >
        Photos
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Choose the cover players see in discovery. Add up to 12 photos; changes
        go live when saved.
      </Typography>
      <Box component="section" aria-label="Cover preview">
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: "center", mb: 1, flexWrap: "wrap" }}
        >
          <Typography variant="subtitle2" sx={{ mr: 1 }}>
            Cover preview
          </Typography>
          {cover
            ? (["desktop", "mobile"] as const).map((shape) => (
                <Button
                  key={shape}
                  size="small"
                  aria-pressed={previewShape === shape}
                  onClick={() => setPreviewShape(shape)}
                  variant={previewShape === shape ? "outlined" : "text"}
                >
                  {shape === "desktop"
                    ? "Desktop thumbnail"
                    : "Mobile thumbnail"}
                </Button>
              ))
            : null}
        </Stack>
        {cover ? (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            sx={{ alignItems: { sm: "center" } }}
          >
            <Box
              component="img"
              src={cover.url}
              alt={cover.alt_text || "Facility cover preview"}
              sx={{
                width: previewShape === "desktop" ? 200 : 120,
                maxWidth: "100%",
                aspectRatio: previewShape === "desktop" ? "4 / 3" : "1 / 1",
                objectFit: "cover",
                borderRadius: 1.5,
              }}
            />
            <Typography variant="body2" color="text.secondary">
              Discovery crops the cover to fit its thumbnail. Keep important
              details near the centre.
            </Typography>
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Without a cover, players see a compact text-only card. You can add a
            photo later.
          </Typography>
        )}
      </Box>
      {fields.map((photo, index) => (
        <Stack
          key={photo.id}
          spacing={1}
          direction={{ xs: "column", sm: "row" }}
          sx={{ alignItems: { sm: "center" } }}
        >
          <Box
            component="img"
            src={photo.url}
            alt=""
            sx={{ width: 128, height: 88, objectFit: "cover", borderRadius: 1 }}
          />
          <TextField
            {...register(`photos.${index}.alt_text`, {
              validate: (value) =>
                Boolean(value.trim()) ||
                "Describe this photo for people using screen readers.",
              maxLength: {
                value: 300,
                message: "Use 300 characters or fewer.",
              },
            })}
            label={`Photo ${index + 1} description`}
            required
            size="small"
            fullWidth
            disabled={busy !== null}
            error={Boolean(errors.photos?.[index]?.alt_text)}
            helperText={errors.photos?.[index]?.alt_text?.message}
          />
          <Stack
            direction="row"
            spacing={0.5}
            useFlexGap
            sx={{ flexWrap: "wrap" }}
          >
            {index === 0 ? (
              <Chip size="small" label="Cover" />
            ) : (
              <Button
                size="small"
                variant="outlined"
                disabled={busy !== null}
                aria-label={`Set photo ${index + 1} as cover`}
                onClick={() => {
                  move(index, 0);
                  setSaved(false);
                }}
              >
                Set as cover
              </Button>
            )}
            <Button
              size="small"
              disabled={busy !== null || index === 0}
              aria-label={`Move photo ${index + 1} earlier`}
              onClick={() => {
                move(index, index - 1);
                setSaved(false);
              }}
            >
              Earlier
            </Button>
            <Button
              size="small"
              disabled={busy !== null || index === fields.length - 1}
              aria-label={`Move photo ${index + 1} later`}
              onClick={() => {
                move(index, index + 1);
                setSaved(false);
              }}
            >
              Later
            </Button>
            <Button
              size="small"
              color="error"
              disabled={busy !== null}
              aria-label={`Remove photo ${index + 1}`}
              onClick={() => {
                remove(index);
                setSaved(false);
              }}
            >
              Remove
            </Button>
          </Stack>
        </Stack>
      ))}
      {error && <Alert severity="error">{error}</Alert>}
      {saved && !isDirty && <Alert severity="success">Photos saved.</Alert>}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
        <Button
          component="label"
          size="small"
          variant="outlined"
          disabled={busy !== null || fields.length >= 12}
        >
          {busy === "upload" ? "Uploading…" : "Upload photo"}
          <input
            type="file"
            hidden
            aria-label="Upload photo"
            accept="image/jpeg,image/png,image/webp"
            disabled={busy !== null || fields.length >= 12}
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = "";
              if (file) void upload(file);
            }}
          />
        </Button>
        <Button
          type="button"
          size="small"
          variant="contained"
          disabled={busy !== null || !isDirty}
          onClick={() => void handleSubmit(save)()}
        >
          {busy === "save" ? "Saving…" : "Save photos"}
        </Button>
        <Button
          type="button"
          size="small"
          disabled={busy !== null || !isDirty}
          onClick={() => {
            reset({ photos: initialPhotos });
            setSaved(false);
            setError(null);
          }}
        >
          Discard changes
        </Button>
      </Stack>
    </Stack>
  );
}
